import React, { useState, useEffect, useContext } from 'react';
import { withRouter } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { jsonrepair } from 'jsonrepair';

import config from '../config';
import { useQuery, useMutation } from '../hooks/graph';
import { SetupContext } from '../contexts/setup';
// import { formControlInvalid } from '../components/forms';
import Icon from '../components/icon';
import Spinner from '../components/spinner';
import { setClipboard, enableTooltips } from '../utils/misc';

import MeDocumentationResponseSuccess from './me-documentation-response-success';
import MeDocumentationResponseError from './me-documentation-response-error';
import MeDocumentationExamples from './me-documentation-examples';

const controller = new AbortController();

/* eslint-disable complexity */ // temporal
function MeDocumentation({ match, location }) {
	const { providers } = useContext(SetupContext);
	const [ { data: dataConnectors } ] = useQuery('connectors', controller);
	const [ orderPreview ] = useMutation('orderPreview', controller);

	const { register, handleSubmit, getValues, setValue, clearErrors, formState: { errors } } = useForm();
	const [ state, setState ] = useState({});
	const reference = state.reference;

	// reserved
	// const getOrderUrl = () => {
	// 	const url = new URL(config.base + '/me/documentation/' + state.provider.name);

	// 	try {
	// 		url.search = new URLSearchParams(parseOrder(getValues().order));
	// 		return url.toString();
	// 	}
	// 	catch (err) {
	// 		setState({...state, response: err});
	// 	}
	// }

	// experimental
	const getClassActions = (_class, actions) => {
		const optionActions = config.order.action.lists.option;

		if (actions.includes(optionActions[0])) {
			return _class === 'option' ? optionActions :
				actions.filter(item => !optionActions.includes(item));
		}

		return actions;
	};

	const parseOrder = order => {
		try {
			return JSON.parse(order);
		}

		catch (err) {
			try {
				const correctOrder = jsonrepair(order);
				setValue('order', correctOrder); // fix synthax error / obsoleted: ...JSON.stringify(params, null, 2)
				return JSON.parse(correctOrder);
			}
			catch {
				// reserved
				// const line = err.message.match(/line (\d+)/)?.[1];
				// const position = err.message.match(/position (\d+)/)?.[1];

				// TODO
				// highlight?

				throw err;
			}
		}
	};

	const renderOrder = (params = {}) => {
		let _class;

		if (params.class) {
			if (['future', 'option', 'warrant', 'bond'].includes(params.class)) { // "bond" too?
				_class = params.class;
			}

			if (state.provider.name === 'ibkr' && params.class === 'cfd') {
				_class = params.class;
			}
		}

		// preserve the order for a nicer output
		return JSON.stringify({
			symbol: params.symbol ?? config.order.class.examples[_class],
			action: params.action,
			...(_class && {class: _class}),
			...(!['close', 'cancel'].includes(params.action) && {quantity: params.quantity ? parseFloat(params.quantity) : reference.quantity.examples[0]}),
			...(['limit', 'stop_limit'].includes(params.type) && {limit_price: params.limit_price ? parseFloat(params.limit_price) : reference.limit_price.examples[0]}),
			...(['stop', 'stop_limit'].includes(params.type) && {stop_price: params.stop_price ? parseFloat(params.stop_price) : reference.stop_price.examples[0]}),
			...(params.duration && {duration: params.duration}),
			...(reference.advancedType?.values.includes(params.type) && {type: params.type}),
		}, null, 2);
	};

	const setOrder = (name, value, params = {}) => {
		switch (name) {
			case 'symbol': {
				const symbolIndex = reference.symbol.examples.indexOf(value);
				const symbolClass = reference.symbol.examplesClass?.[symbolIndex] || reference.class.values[0];

				const classActions = getClassActions(symbolClass, reference.action.values);
				params = setOrder('action', classActions.includes(params.action) ?
					params.action : classActions[0], params);

				return {
					...params,
					...(symbolClass && {class: symbolClass}),
					symbol: value,
				};
			}

			case 'class': {
				// TODO config "default" examples for all classes
				const classSymbol = reference.class.values.length > 1 ?
					reference.symbol.examples[reference.symbol.examplesClass?.indexOf(value)] || reference.symbol.examples[reference.class.values.indexOf(value)]
					:
					reference.symbol.examples[reference.symbol.examplesAccount?.indexOf(state.providerAccountId) || 0];

				const classActions = getClassActions(value, reference.action.values);
				params = setOrder('action', classActions.includes(params.action) ?
					params.action : classActions[0], params);

				return {
					...params,
					class: value,
					symbol: classSymbol,
					type: params.type ?? reference.type.values[0], // experimental
				};
			}

			case 'type': {
				return {
					...params,
					...(['close', 'cancel'].includes(params.action) && {action: 'buy'}),
					type: value,
				};
			}

			case 'action': {
				return {
					...params,
					...(['close', 'cancel'].includes(value) && {type: 'market'}),
					action: value,
				};
			}

			default:
				return {
					...params,
					[name]: value,
				};
		}

		// reserved / JSON.parse(JSON.stringify(params)) // cleanup undefined params etc
	};

	const onChangeOrder = (name, value, event) => {
		const params = setOrder(name, value, state.params);

		setState({...state, params});
		setValue('order', renderOrder(params));

		event?.type === 'click' && document.getElementById('playground').scrollIntoView();
	};

	const onChangeProvider = (providerName, connectedProviderNames, extraProviderNames) => {
		const provider = providers[providerName];
		const providerReference = provider?.reference || {};

		const providerConnectorAccounts = dataConnectors.connectors.
			filter(item => item.providerName === provider?.name).
			map(item => item.accounts).
			flat();

		const providerAccounts = providerConnectorAccounts.
			filter((item, index, accounts) => index ==
				accounts.findLastIndex(itemLast => itemLast.id === item.id)
			);

		const limitPriceRequired = !providerReference.type?.values?.includes('market');
		const typeValues = providerReference.type?.values.filter(item => config.order.type.list.includes(item)) || config.order.type.list;

		const advancedType = {...providerReference.type,
			values: providerReference.type?.values.filter(item => !typeValues.includes(item)),
		};

		setState({...state,
			provider,
			providerAccounts, // across all the connectors
			providerAccountId: providerAccounts[0]?.id, // selected account if any

			connectedProviderNames: connectedProviderNames || state.connectedProviderNames || [],
			extraProviderNames: extraProviderNames || state.extraProviderNames || [],

			response: undefined,

			reference: {...providerReference,
				type: {values: typeValues}, // experimental

				symbol: {...providerReference.symbol,
					required: true,
					examples: providerReference.symbol?.examples || ['AAPL'],
				},
				action: {...providerReference.action,
					required: true,
					values: providerReference.action?.values || ['buy', 'sell'],
					comments: [...(providerReference.action?.comments || [])],
				},
				quantity: {...providerReference.quantity,
					required: true,
					examples: providerReference.quantity?.examples || [1, '"2 + 3"'],
					comments: [
						...(providerReference.action?.values.includes('close') ? ['Not required for "cancel" and "close" orders'] : []),
						...(providerReference.quantity_type?.values.includes('percent_of_equity') ? ['Set to 50 for 50% of equity for "percent_of_equity" orders.'] : []),
						...(providerReference.quantity?.comments || []),
					],
				},
				extra: {...providerReference.extra,
					comments: ['Custom native brokerage order properties (see the broker API)', ...(providerReference.extra?.comments || [])],
				},
				quantity_type: {...providerReference.quantity_type,
					examples: providerReference.quantity_type?.examples || ['fixed'],
					comments: ['By default: "fixed"', ...(providerReference.quantity_type?.comments || [])],
				},
				limit_price: {...providerReference.limit_price,
					required: limitPriceRequired,
					examples: providerReference.limit_price?.examples || [123.98, '"123 + 5"'],
					comments: [limitPriceRequired ? 'Required' : 'For "limit" orders only', ...(providerReference.limit_price?.comments || [])], // "required": indicate explicitly
				},
				stop_price: {...providerReference.stop_price,
					examples: providerReference.stop_price?.examples || [123.98, '"123 + 5"'],
					comments: ['For "stop" orders only', ...(providerReference.stop_price?.comments || [])],
				},
				...(providerReference.stop_price_distance && {stop_price_distance: {...providerReference.stop_price_distance,
					examples: providerReference.stop_price_distance?.examples || [123, '"123 + 5"'],
					comments: ['For "stop" orders only', ...(providerReference.stop_price_distance?.comments || [])],
				}}),
				...(providerReference.stop_price_percent && {stop_price_percent: {...providerReference.stop_price_percent,
					examples: providerReference.stop_price_percent?.examples || [1.23, '"1.23 + 5"'],
					comments: ['For "stop" orders only', ...(providerReference.stop_price_percent?.comments || [])],
				}}),
				...(providerReference.take_profit_price && {take_profit_price: {...providerReference.take_profit_price,
					examples: providerReference.take_profit_price?.examples || [123.98, '"123 + 5"'],
					comments: ['For "take profit" bracket (conditional) orders only', ...(providerReference.take_profit_price?.comments || [])],
				}}),
				...(providerReference.stop_loss_price && {stop_loss_price: {...providerReference.stop_loss_price,
					examples: providerReference.stop_loss_price?.examples || [123.98, '"123 + 5"'],
					comments: ['For "stop loss" bracket (conditional) orders only', ...(providerReference.stop_loss_price?.comments || [])],
				}}),
				...(providerReference.stop_loss_price_distance && {stop_loss_price_distance: {...providerReference.stop_loss_price_distance,
					examples: providerReference.stop_loss_price_distance?.examples || [123, '"123 + 5"'],
					comments: ['For "trailing stop loss" bracket (conditional) orders only', ...(providerReference.stop_loss_price_distance?.comments || [])],
				}}),
				...(providerReference.stop_loss_price_percent && {stop_loss_price_percent: {...providerReference.stop_loss_price_percent,
					examples: providerReference.stop_loss_price_percent?.examples || [1.23, '"123 + 5"'],
					comments: ['For "trailing stop loss" bracket (conditional) orders only', ...(providerReference.stop_loss_price_percent?.comments || [])],
				}}),
				...(advancedType.values.length && {advancedType: {...advancedType,
					examples: advancedType.examples || [`"${advancedType.values[0]}"`],
					comments: ['For advanced order types only', ...(advancedType.comments || [])],
				}}),
				...(providerReference.duration && {duration: {...providerReference.duration,
					comments: [...(providerReference.duration?.comments || []), 'Definitions, ' + providerReference.duration.values.map(item => item + ': ' + config.order.duration.definitions[item]).join(', ')],
				}}),
				...(providerReference.client_id && {client_id: {...providerReference.client_id,
					examples: providerReference.client_id?.examples || ['"ID12345"'],
					comments: [...(providerReference.client_id?.comments || []), 'Extra custom order id'],
				}}),
				...(providerReference.parent_id && {parent_id: {...providerReference.parent_id,
					examples: providerReference.parent_id?.examples || ['"ID12345"'],
					comments: [...(providerReference.parent_id?.comments || []), 'For separate orders associated with the "parent" order (to set up a OTO/OCO order groups)'],
				}}),
				...(providerReference.group?.values?.length > 1 ? {group: {...providerReference.group,
					examples: providerReference.group?.examples || [`"${providerReference.group.values[0]}"`],
					comments: ['For multileg orders only: relation between legs; By default: "combo" (aka spread)', ...(providerReference.group?.comments || [])],
				}} : {group: null}),
				...(providerReference.group && {legs: { // yes, it's triggered by "group" reference
					comments: [`Properties of legs for multileg ${providerReference.group?.values?.length > 1 ? '' : 'combo (aka spread)'} orders`],
				}}),
			},
		});
	};

	const onChangeProviderAccount = providerAccountId => {
		setState({...state, providerAccountId});
	};

	const onSubmit = values => {
		let dataOrder;

		const connector = dataConnectors.connectors.find(
			item => item.providerName === state.provider?.name && item.accounts.find(
				itemAccount => itemAccount.id === state.providerAccountId
			)
		);

		if (!connector) {
			setState({...state, response: 'Please, connect the broker to submit test orders'});
			return;
		}

		if (!connector.isActive) {
			setState({...state, response: 'Please, reconnect the broker'});
			return;
		}

		try {
			dataOrder = parseOrder(values.order);
		}
		catch (err) {
			setState({...state, response: err});
			return;
		}

		setState({...state, response: null, isLoading: true}); // uxui: to pre-hide previous reply to have a timespan between the two

		dataOrder && orderPreview({
			connectorId: connector.id,
			accountId: state.providerAccountId || null,
			data: JSON.stringify(dataOrder), // yeah, re-strigified back one
		}, controller).then(({ data: { orderPreview: orderPreviewData } }) => {
			Object.keys(orderPreviewData).filter(item => item.startsWith('_') || orderPreviewData[item] === null).forEach(item => delete orderPreviewData[item]);
			setState({...state, response: orderPreviewData, request: values.order, isLoading: false});

		}).catch(({ message, name }) => {
			// experimental
			const err = new Error(message);
			err.name = name;

			setState({...state, response: err, isLoading: false});
		});
	};

	useEffect(() => {
		if (!state.provider) {
			return;
		}

		const params = setOrder('class',
			reference.class.values.includes('stock') ? 'stock' : reference.class.values[0] // experimental
		);

		setState({...state, params});
		setValue('order', renderOrder(params, {
			// experimental: default params from the url for the first render only
			...(!getValues('order') && Object.fromEntries(new URLSearchParams(location.search))),
		}));

		clearErrors('submit');
	}, [state.provider, state.providerAccountId]);

	useEffect(() => {
		if (!dataConnectors) { return; } // keep it, to prevent rendering sides

		const providerNames = Object.keys(providers).filter(item => providers[item].status);
		const userProviderNames = dataConnectors.connectors.map(item => item.providerName);

		const connectedProviderNames = providerNames.filter(item => userProviderNames.includes(item));
		const extraProviderNames = providerNames.filter(item => !userProviderNames.includes(item));

		onChangeProvider(
			connectedProviderNames.find(item => item === match.params?.providerName)
				|| connectedProviderNames[0] || extraProviderNames[0],
			connectedProviderNames,
			extraProviderNames,
		);
	}, [dataConnectors]);

	useEffect(() => {
		if (!state.params) { return; }

		// reserved
		// // critical: after displaying the content
		// location.hash && document.getElementById(location.hash.substring(1))?.
		// 	scrollIntoView({behavior: 'smooth'});

		// critical: after rendering of the "toggling" elemenent
		enableTooltips();
	}, [!!state.params]);

	useEffect(() => () => controller.abort(), []); // abort on unmount

	return (
		<div id="documentation">
			<h3 className="my-3">Documentation</h3>

			<div>
				Choose a broker to view <a href="#request">order parameters</a>, see <a href="#examples">examples</a> and use the <a href="#playground">playground</a> to place test orders in a&nbsp;&quot;preview&quot; mode.
				The list of available order parameters and their values may differ from a broker to broker.
			</div>

			{ state.reference ? (
				<div id="playground" className="mt-3">
					{
						state.connectedProviderNames.length || state.extraProviderNames.length ? (
							<div>
								<select onChange={event => onChangeProvider(event.target.value)} defaultValue={match.params.providerName} name="providerName" id="providerName" className="form-select form-select-lg border-primary">
									{ state.connectedProviderNames.map(item => <option key={providers[item].name} value={providers[item].name}>{providers[item].title}</option>) }
									{ state.connectedProviderNames.length && state.extraProviderNames.length && <option key="separator" value={state.provider?.name}>- - - - -</option> }
									{ state.extraProviderNames.map(item => <option key={providers[item].name} value={providers[item].name}>{providers[item].title}</option>) }
								</select>
							</div>
						) : false
					}

					{
						state.providerAccounts.length > 1 ? (
							<div className="mt-2">
								<select onChange={event => onChangeProviderAccount(event.target.value)} defaultValue={state.providerAccounts[0].id} name="providerAccountId" id="providerAccountId" className="form-select">
									{
										state.providerAccounts.map(itemAccount => {
											const title = itemAccount.dataTitle || itemAccount.id;
											const titleSuffix = [itemAccount.name, itemAccount.dataType, itemAccount.dataCurrency].filter(item => !!item).join(', ');
											return <option key={itemAccount.id} value={itemAccount.id}>{title || 'Default'} {titleSuffix ? '/' : ''} {titleSuffix || ''}</option>;
										})
									}
								</select>
							</div>
						) : false
					}

					<div className="fw-bold mt-4">Playground</div>

					{
						state.params && (
							<div className="mt-2">
								Select { reference.class.values.length > 1 ? 'asset class,' : false } order type and action to render a sample order below:

								<div className="row mt-2">
									{
										reference.class.values.length > 1 ? (
											<div className="col-12 col-md-4 mb-2 mb-md-0">
												<div className="input-group input-group-sm">
													{/* <label className="visually-hidden">Class</label> */}
													<select value={state.params.class} onChange={event => onChangeOrder('class', event.target.value)} name="orderClass" id="orderClass" className="form-select">
														{ reference.class.values.map(item => <option key={item} value={item}>{config.order.class.labels[item]}</option>) }
													</select>
													{/*
														<div className="input-group-text" onClick={event => { event.preventDefault(); window.Reamaze?.popup(`/kb/${state.provider.name}/${state.provider.name}-${state.params.class}`); }} role="button">
															<Icon.Question />
														</div>
													*/}
												</div>
											</div>
										) : false
									}

									<div className="col-12 col-md-4 mb-2 mb-md-0">
										<label className="visually-hidden" htmlFor="orderType">Type</label>
										<select value={state.params.type} onChange={event => onChangeOrder('type', event.target.value)} name="orderType" id="orderType" className="form-select form-select-sm">
											{ reference.type.values.map(item => <option key={item} value={item}>{config.order.type.labels[item]}</option>) }
										</select>
									</div>

									<div className="col-12 col-md-4">
										<label className="visually-hidden" htmlFor="orderAction">Action</label>
										<select value={state.params.action} onChange={event => onChangeOrder('action', event.target.value)} name="orderAction" id="orderAction" className="form-select form-select-sm">
											{ getClassActions(state.params.class, reference.action.values).map(item => <option key={item} value={item}>{config.order.action.labels[item]}</option>) }
										</select>
									</div>
								</div>
							</div>
						)
					}

					{
						state.params && (
							<div className="mt-3">
								<form onSubmit={handleSubmit(onSubmit)} onFocus={() => errors.submit && clearErrors('submit')} style={{position: 'relative'}}>
									{/*
									<a onClick={() => setClipboard(getOrderUrl())} role="button" title="Share" style={{position: 'absolute', top: '0.5em', right: '0.75em'}}><Icon.Gear /></a>
									*/}

									<div className="position-relative">
										<button
											type="button"
											className="btn btn-light position-absolute"
											data-bs-toggle="reserved-tooltip"
											data-bs-title="Copy to clipboard"
											style={{top: '0.75em', right: '0.75em', zIndex: 11}}
											onClick={() => setClipboard(getValues('order'))}
										>
											<Icon.Copy />
										</button>

										<a
											href="#examples"
											role="button"
											className="btn btn-light position-absolute"
											style={{bottom: '0.75em', right: '0.75em', zIndex: 10}}
										>
											Examples&nbsp;<Icon.ArrowRight />
										</a>

										<textarea {...register('order')} rows="8" className="form-control font-monospace" style={{fontSize: '90%'}}></textarea>
									</div>

									{/*
									<div className="btn-group w-100" role="group" aria-label="">
										{
											state.platforms.map(item => [
												<input key="input" {...register('platform')} value={item} id={item} type="radio" className="btn-check" />
											,
												<label key="label" className="btn btn-outline-secondary rounded-top-0" htmlFor={item}>{item}</label>
											])
										}
									</div>
									*/}

									<div className="d-grid gap-2"><button type="submit" className="btn btn-outline-primary btn-block mt-3"><Spinner show={state.isLoading}>Test</Spinner></button></div>

									{
										state.response ? (
											<div className="position-relative">
												<div
													role="button"
													className={
														'btn border-0 position-absolute ' +
														(state.response instanceof Error ? 'btn-outline-danger' : 'btn-outline-success')
													}
													style={{top: '0.75em', right: '0.75em', zIndex: 10}}
												>
													{
														{
															201: <Icon.Smile />,
															202: <Icon.Surprise />,
															203: <Icon.Wink />,
															401: <Icon.Frown />,
															402: <Icon.Tear />,
															403: <Icon.Grimace />,
														}[Math.ceil(Math.random() * 3) +
															(state.response instanceof Error ? 400 : 200)
														]
													}
												</div>

												{
													state.response instanceof Error ? (
														<div className="alert alert-danger mt-3">
															<pre className="mb-0">
																{JSON.stringify({
																	// id: 'ecaf9555-c414-48f9-b125-f67e8aad9cd6',
																	status: state.response.name,
																	message: state.response.message,
																}, null, 2)}
															</pre>
														</div>
													) : (
														<div>
															{
																state.response.status ? (
																	<div className="alert alert-success mt-3">
																		<pre className="mb-0">{JSON.stringify(state.response, null, 2)}</pre>
																	</div>
																) : (
																	<div className="alert alert-warning mt-3">
																		<pre className="mb-0">{state.response}</pre>
																	</div>
																)
															}
														</div>
													)
												}
											</div>
										) : false
									}
								</form>
							</div>
						)
					}

					<div id="request" className="mt-4">
						<table className="table table-hover">
							<thead>
								<tr>
									<th scope="col" width="17%">Instruction</th>
									<th scope="col" width="23%">Value</th>
									<th scope="col" width="30%">Examples<span className="d-inline d-lg-none">, Comments</span></th>
									<th scope="col" width="30%" className="d-none d-lg-table-cell">Comments</th>
								</tr>
							</thead>
							<tbody>
								<tr className={reference.symbol?.required ? '' : 'text-secondary'}>
									<td>symbol</td>
									<td><i>string</i></td>
									<td>
										{reference?.symbol?.examples.map((item, index) =>
											<div key={index} className="text-nowrap">
												<span role="button" onClick={onChangeOrder.bind(this, 'symbol', item)}>&quot;{item}&quot;</span>
												{reference?.symbol?.examplesClass?.[index] ? <span className="text-secondary">&nbsp;({config.order.class.labels[reference.symbol.examplesClass[index]]})</span> : ''}
												{reference?.symbol?.examplesAccount?.[index] ? <span className="text-secondary">&nbsp;({reference.symbol.examplesAccount[index]})</span> : ''}
												{reference?.symbol?.examplesComment?.[index] ? <span className="text-secondary">&nbsp;({reference.symbol.examplesComment[index]})</span> : ''}
												{',\n'}
												{/* {index + 1 < reference?.symbol?.examples.length ? ',\n' : ''} */}
											</div>
										)}

										<div key="trendspider-bot" className="text-nowrap"><small>&quot;%bot_symbol%&quot;&nbsp;<span className="text-secondary">{'(TrendSpiderBot),\n'}</span></small></div>
										<div key="trendspider-alert" className="text-nowrap"><small>&quot;%alert_symbol%&quot;&nbsp;<span className="text-secondary">{'(TrendSpiderAlert),\n'}</span></small></div>
										<div key="tradingview" className="text-nowrap"><small>{'"{{ticker}}"'}&nbsp;<span className="text-secondary">{'(TradingView)\n'}</span></small></div>

										<div className="d-block d-lg-none mt-3"><small>{reference.symbol.comments?.join('; ')}</small></div>
									</td>
									<td className="d-none d-lg-table-cell" style={{whiteSpace: 'pre-line'}}>{reference.symbol.comments?.join('; \n')}</td>
								</tr>

								{
									reference.class?.comments && ( // yep, such a weird condition
										<tr>
											<td className={reference.class?.required ? '' : 'text-secondary'}>class</td>
											<td className={reference.class?.required ? '' : 'text-secondary'}>{reference.class.values.join(', ')}</td>
											<td className={reference.class?.required ? '' : 'text-secondary'}>&quot;{reference.class.values.includes('future') ? 'future' : reference.class.values[0]}&quot; <div className={'d-block d-lg-none mt-3 ' + (reference.class?.required ? '' : 'text-secondary')}><small>{reference.class.comments?.join('; ')}</small></div></td>
											<td className={'d-none d-lg-table-cell ' + (reference.class?.required ? '' : 'text-secondary')}>{reference.class.comments?.join('; ')}</td>
										</tr>
									)
								}

								<tr className={reference.action?.required ? '' : 'text-secondary'}>
									<td>action</td>
									<td>{reference.action.values.join(', ')}</td>
									<td>
										<div className="text-nowrap">&quot;{reference.action.values[0]}&quot;{/* ',\n' */}</div>
										{/* <div key="tradingview" className="text-nowrap"><small>{'"{{strategy.order.action}}"'}&nbsp;<span className="text-secondary">{'(TradingView)\n'}</span></small></div> */}
										<div className="d-block d-lg-none mt-3"><small>{reference.action.comments?.join('; ')}</small></div>
									</td>
									<td className="d-none d-lg-table-cell">{reference.action.comments?.join('; ')}</td>
								</tr>

								<tr className={reference.quantity?.required ? '' : 'text-secondary'}>
									<td>quantity</td>
									<td><i>number,<br/>expression</i></td>
									<td>
										<div className="text-nowrap">{reference.quantity.examples[0]}{/* ',\n' */}</div>
										{/* <div key="tradingview" className="text-nowrap"><small>{'{{strategy.position_size}}'}&nbsp;<span className="text-secondary">{'(TradingView)\n'}</span></small></div> */}
										{/* <div key="tradingview" className="text-nowrap"><small>{'"{{strategy.order.contracts}}"'}&nbsp;<span className="text-secondary">{'(TradingView)\n'}</span></small></div> */}

										<div className="d-block d-lg-none mt-3"><small>{reference.quantity.comments?.join('; ')}</small></div>
									</td>
									<td className="d-none d-lg-table-cell">{reference.quantity.comments?.join('; ')}</td>
								</tr>

								{
									reference.quantity_type?.values?.length && (
										<tr>
											<td className={reference.quantity_type?.required ? '' : 'text-secondary'}>quantity_type</td>
											<td className={reference.quantity_type?.required ? '' : 'text-secondary'}>{reference.quantity_type.values.join(', ')}</td>
											<td className={reference.quantity_type?.required ? '' : 'text-secondary'}>&quot;{reference.quantity_type.examples[0]}&quot; <div className={'d-block d-lg-none mt-3 ' + (reference.quantity_type?.required ? '' : 'text-secondary')}><small>{reference.quantity_type.comments?.join('; ')}</small></div></td>
											<td className={'d-none d-lg-table-cell ' + (reference.quantity_type?.required ? '' : 'text-secondary')}>{reference.quantity_type.comments?.join('; ')}</td>
										</tr>
									)
								}

								{
									reference.type?.values?.includes('limit') && (
										<tr>
											<td className={reference.limit_price?.required ? '' : 'text-secondary'}>limit_price</td>
											<td className={reference.limit_price?.required ? '' : 'text-secondary'}><i>number,<br/>expression</i></td>
											<td className={reference.limit_price?.required ? '' : 'text-secondary'}>{reference.limit_price.examples.map((item, index) => <div key={index}>{item}</div>)} <div className={'d-block d-lg-none mt-3 ' + (reference.limit_price?.required ? '' : 'text-secondary')}><small>{reference.limit_price.comments?.join('; ')}</small></div></td>
											<td className={'d-none d-lg-table-cell ' + (reference.limit_price?.required ? '' : 'text-secondary')}>{reference.limit_price.comments?.join('; ')}</td>
										</tr>
									)
								}

								{
									reference.type?.values?.some(item => (/^stop/).test(item)) && (
										<tr>
											<td className={reference.stop_price?.required ? '' : 'text-secondary'}>stop_price</td>
											<td className={reference.stop_price?.required ? '' : 'text-secondary'}><i>number,<br/>expression</i></td>
											<td className={reference.stop_price?.required ? '' : 'text-secondary'}>{reference.stop_price.examples.map((item, index) => <div key={index}>{item}</div>)} <div className={'d-block d-lg-none mt-3 ' + (reference.stop_price?.required ? '' : 'text-secondary')}><small>{reference.stop_price.comments?.join('; ')}</small></div></td>
											<td className={'d-none d-lg-table-cell ' + (reference.stop_price?.required ? '' : 'text-secondary')}>{reference.stop_price.comments?.join('; ')}</td>
										</tr>
									)
								}

								{
									reference.stop_price_distance && (
										<tr>
											<td className={reference.stop_price_distance_?.required ? '' : 'text-secondary'}>stop_price_distance</td>
											<td className={reference.stop_price_distance?.required ? '' : 'text-secondary'}><i>number,<br/>expression</i></td>
											<td className={reference.stop_price_distance?.required ? '' : 'text-secondary'}>{reference.stop_price_distance.examples.map((item, index) => <div key={index}>{item}</div>)} <div className={'d-block d-lg-none mt-3 ' + (reference.stop_price_distance?.required ? '' : 'text-secondary')}><small>{reference.stop_price_distance.comments?.join('; ')}</small></div></td>
											<td className={'d-none d-lg-table-cell ' + (reference.stop_price_distance?.required ? '' : 'text-secondary')}>{reference.stop_price_distance.comments?.join('; ')}</td>
										</tr>
									)
								}

								{
									reference.stop_price_percent && (
										<tr>
											<td className={reference.stop_price_percent?.required ? '' : 'text-secondary'}>stop_price_percent</td>
											<td className={reference.stop_price_percent?.required ? '' : 'text-secondary'}><i>number,<br/>expression</i></td>
											<td className={reference.stop_price_percent?.required ? '' : 'text-secondary'}>{reference.stop_price_percent.examples.map((item, index) => <div key={index}>{item}</div>)} <div className={'d-block d-lg-none mt-3 ' + (reference.stop_price_percent?.required ? '' : 'text-secondary')}><small>{reference.stop_price_percent.comments?.join('; ')}</small></div></td>
											<td className={'d-none d-lg-table-cell ' + (reference.stop_price_percent?.required ? '' : 'text-secondary')}>{reference.stop_price_percent.comments?.join('; ')}</td>
										</tr>
									)
								}

								{
									reference.take_profit_price && (
										<tr>
											<td className="text-secondary">take_profit_price</td>
											<td className="text-secondary"><i>number,<br/>expression</i></td>
											<td className="text-secondary">{reference.take_profit_price.examples.map((item, index) => <div key={index}>{item}</div>)} <div className="d-block d-lg-none mt-3 text-secondary"><small>{reference.take_profit_price.comments?.join('; ')}</small></div></td>
											<td className="d-none d-lg-table-cell text-secondary">{reference.take_profit_price.comments?.join('; ')}</td>
										</tr>
									)
								}

								{
									reference.stop_loss_price && (
										<tr>
											<td className="text-secondary">stop_loss_price</td>
											<td className="text-secondary"><i>number,<br/>expression</i></td>
											<td className="text-secondary">{reference.stop_loss_price.examples.map((item, index) => <div key={index}>{item}</div>)} <div className="d-block d-lg-none mt-3 text-secondary"><small>{reference.stop_loss_price.comments?.join('; ')}</small></div></td>
											<td className="d-none d-lg-table-cell text-secondary">{reference.stop_loss_price.comments?.join('; ')}</td>
										</tr>
									)
								}

								{
									reference.stop_loss_price_distance && (
										<tr>
											<td className="text-secondary">stop_loss<wbr />_price_distance</td>
											<td className="text-secondary"><i>number,<br/>expression</i></td>
											<td className="text-secondary">{reference.stop_loss_price_distance.examples.map((item, index) => <div key={index}>{item}</div>)} <div className="d-block d-lg-none mt-3 text-secondary"><small>{reference.stop_loss_price_distance.comments?.join('; ')}</small></div></td>
											<td className="d-none d-lg-table-cell text-secondary">{reference.stop_loss_price_distance.comments?.join('; ')}</td>
										</tr>
									)
								}

								{
									reference.stop_loss_price_percent && (
										<tr>
											<td className="text-secondary">stop_loss<wbr />_price_percent</td>
											<td className="text-secondary"><i>number,<br/>expression</i></td>
											<td className="text-secondary">{reference.stop_loss_price_percent.examples.map((item, index) => <div key={index}>{item}</div>)} <div className="d-block d-lg-none mt-3 text-secondary"><small>{reference.stop_loss_price_percent.comments?.join('; ')}</small></div></td>
											<td className="d-none d-lg-table-cell text-secondary">{reference.stop_loss_price_percent.comments?.join('; ')}</td>
										</tr>
									)
								}

								{
									reference.advancedType && (
										<tr>
											<td className="text-secondary">type</td>
											<td className="text-secondary">{reference.advancedType.values.map((item, index) => <span key={item}>{ index ? ', ' : ''}<span role="button" onClick={onChangeOrder.bind(this, 'type', item)}>{item}</span></span>)}</td>
											<td className="text-secondary">&quot;<span role="button" onClick={onChangeOrder.bind(this, 'type', reference.advancedType.values[0])}>{reference.advancedType.values[0]}</span>&quot; <div className="d-block d-lg-none mt-3 text-secondary"><small>{reference.advancedType.comments?.join('; ')}</small></div></td>
											<td className="d-none d-lg-table-cell text-secondary">{reference.advancedType.comments?.join('; ')}</td>
										</tr>
									)
								}

								{
									reference.duration && (
										<tr>
											<td className="text-secondary">duration</td>
											<td className="text-secondary">{reference.duration.values.map((item, index) => <span key={item}>{ index ? ', ' : ''}<span role="button" onClick={onChangeOrder.bind(this, 'duration', item)}>{item}</span></span>)}</td>
											<td className="text-secondary">&quot;<span role="button" onClick={onChangeOrder.bind(this, 'duration', reference.duration.values[0])}>{reference.duration.values[0]}</span>&quot; <div className="d-block d-lg-none mt-3 text-secondary"><small>{reference.duration.comments?.join('; ')}</small></div></td>
											<td className="d-none d-lg-table-cell text-secondary">{reference.duration.comments?.join('; ')}</td>
										</tr>
									)
								}

								{
									reference.client_id && (
										<tr>
											<td className="text-secondary">client_id</td>
											<td className="text-secondary"><i>string or number</i></td>
											<td className="text-secondary">{reference.client_id.examples[0]} <div className="d-block d-lg-none mt-3 text-secondary"><small>{reference.client_id.comments?.join('; ')}</small></div></td>
											<td className="d-none d-lg-table-cell text-secondary">{reference.client_id.comments?.join('; ')}</td>
										</tr>
									)
								}

								{
									reference.parent_id && (
										<tr>
											<td className="text-secondary">parent_id</td>
											<td className="text-secondary"><i>string or number</i></td>
											<td className="text-secondary">{reference.parent_id.examples[0]} <div className="d-block d-lg-none mt-3 text-secondary"><small>{reference.parent_id.comments?.join('; ')}</small></div></td>
											<td className="d-none d-lg-table-cell text-secondary">{reference.parent_id.comments?.join('; ')}</td>
										</tr>
									)
								}

								{
									// don't display if none or only one group type enabled
									reference.group?.values?.length && (
										<tr>
											<td className="text-secondary">group</td>
											<td className="text-secondary"><i>string</i></td>
											<td className="text-secondary">{reference.group.values.map((item, index) => <span key={item}>{ index ? ', ' : ''}{item}</span>)} <div className="d-block d-lg-none mt-3 text-secondary"><small>{reference.group.comments?.join('; ')}</small></div></td>
											<td className="d-none d-lg-table-cell text-secondary">{reference.group.comments?.join('; ')}</td>
										</tr>
									)
								}

								{
									reference.legs && (
										<tr>
											<td className="text-secondary">symbol[N]<br/>action[N]<br/>quantity[N]<br/>...</td>
											<td className="text-secondary"><i>see related instructions</i></td>
											<td className="text-secondary">{'{"symbol": "A", "action": "buy", "quantity": 1, "symbol[2]": "B", "action[2]": "sell", "quantity[2]": 1, ...}'} <div className="d-block d-lg-none mt-3 text-secondary"><small>{reference.legs.comments?.join('; ')}</small></div></td>
											<td className="d-none d-lg-table-cell text-secondary">{reference.legs.comments?.join('; ')}</td>
										</tr>
									)
								}

								<tr>
									<td className="text-secondary">extra</td>
									<td className="text-secondary"><i>object</i></td>
									<td className="text-secondary">{'{"a": "A", "b": "B", "x": 123}'} <div className="d-block d-lg-none mt-3 text-secondary"><small>{reference.extra.comments?.join('; ')}</small></div></td>
									<td className="d-none d-lg-table-cell text-secondary">{reference.extra.comments?.join('; ')}</td>
								</tr>
							</tbody>
						</table>

						{
							reference.comments?.length ?
								<div className="mt-2 text-secondary">
									{reference.comments?.join('; ')}
								</div>
								: false
						}

						{
							reference.class?.values.includes('future') ?
								<div className="mt-2 text-secondary"><small>
									Future symbols should be formatted according to the widely accepted standard.
									The inclusion of additional delivery period formatting is permissible: the &quot;continuous&quot; period encoding (N!) can be used, or the period year can be denoted using 1-2-4 digits (2, 22, 2022).
									Examples of these are &quot;MES1!&quot;, &quot;MESU2022&quot;, &quot;MESU22&quot;, &quot;MESU2&quot; or &quot;MES&quot; (as an equivalent to &quot;MES1!&quot;).
								</small></div>
								: false
						}

						{
							reference.class?.values.includes('option') ?
								<div className="mt-2 text-secondary"><small>
									Option symbols should be formatted in line with the OCC standard.
									However, certain simplifications are allowed: you may use an underscore as a delimiter or even omit it altogether, and you may remove the &quot;zeros&quot; in prices for rounded values.
									Examples of these are &quot;AAPL 221121C00145000&quot;, &quot;AAPL_221121C145&quot;, &quot;AAPL221121C145&quot;.
								</small></div>
								: false
						}

						<div className="mt-2 text-secondary"><small>
							Time in force option, when applicable, is set to &quot;good till cancel&quot; (GTC) for limit and stop orders.
						</small></div>

						<div className="mt-2 text-secondary"><small>
							The full set of operators and functions available for expressions can be found <a href="https://www.npmjs.com/package/expr-eval#expression-syntax" target="_blank" rel="noreferrer">here</a>.
						</small></div>

						<div className="mt-2 text-secondary"><small>
							For details on how to use variables provided by different trading platforms, see more in the FAQ: <a href="https://help.signalstack.com/articles/trendspider-alerts-1eadaf83eaa84024" target="_blank" rel="noreferrer">TrendSpider</a>, <a href="https://help.signalstack.com/kb/signal-source-integrations/tradingview-alerts-f07455cf99ed63bd" target="_blank" rel="noreferrer">TradingView</a>.
						</small></div>
					</div>
				</div>
			) : false}

			<div className="mt-5">
				<MeDocumentationResponseSuccess />
			</div>

			<div className="mt-5">
				<MeDocumentationResponseError />
			</div>

			<div className="mt-5">
				{
					// render on condition to preserve "hash" scrolling behavior
					!!state.params && <MeDocumentationExamples onSubmit={onSubmit} provider={state.provider} />
				}
			</div>
		</div>
	);
}
/* eslint-enable complexity */

export default withRouter(MeDocumentation);
